import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import {
  INITIAL_SCANNER_STATE,
  isTypingTarget,
  scannerStep,
  type ScannerBufferState,
} from './scanner-detection';
import type { ProductListItem } from './types';

/**
 * 扫码枪 keyboard-wedge 输入检测（Spec §37、02-ux-ia.md F9）：全局监听
 * "快速连续字符 + 回车"（<50ms/字符）。焦点在普通输入框内时完全不拦截，
 * 让字符正常落入该框——这是 F9 明确要求的行为，不是可选的性能优化。
 *
 * 范围说明：F9 原文写"拦截并打开命令面板"，但命令面板（⌘K 混合搜索）本身
 * 是架构书 §5 定义的独立、更大的跨屏组件，不在 03-roadmap.md 记录的 M4
 * 范围内（M4 只列了"条码键盘输入捕获"这一项）。这里落地一个自包含的最小
 * 扫描结果弹层：命中唯一产品直接给出跳转链接，未命中给出明确提示——
 * F9 描述的两种结果分支都覆盖到了，只是宿主 UI 不是完整的命令面板。
 */
export function BarcodeScannerListener(): React.JSX.Element | null {
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const stateRef = useRef<ScannerBufferState>(INITIAL_SCANNER_STATE);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (isTypingTarget(document.activeElement)) return;

      const { next, scanned } = scannerStep(stateRef.current, {
        key: e.key,
        timestamp: performance.now(),
      });
      stateRef.current = next;
      if (scanned) setScannedCode(scanned);
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!scannedCode) return null;
  return <ScanResultModal code={scannedCode} onClose={() => setScannedCode(null)} />;
}

function ScanResultModal({
  code,
  onClose,
}: {
  readonly code: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isPending } = useQuery({
    queryKey: ['products', 'barcode-lookup', code],
    queryFn: () =>
      apiFetch<{ found: boolean; product?: ProductListItem }>(
        `/products/barcode-lookup/${encodeURIComponent(code)}`,
      ),
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t('scanner.title')}</h2>
        <p className="field-hint">{code}</p>

        {isPending && <p>{t('app.loading')}</p>}
        {!isPending && data?.found && data.product && (
          <div className="row row--between">
            <span>{data.product.name}</span>
            <button
              className="btn btn--primary btn--sm"
              onClick={() => {
                navigate(`/products/${data.product!.id}`);
                onClose();
              }}
            >
              {t('scanner.open')}
            </button>
          </div>
        )}
        {!isPending && !data?.found && <p>{t('scanner.notFound')}</p>}

        <div className="modal__actions">
          <button className="btn btn--ghost" onClick={onClose}>
            {t('app.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
